// D08/D09/D16 — digital commerce assessments.
import { useState } from "react";
import { ResultRow, ToolSection, Note, Radar } from "./ui";

/* ---------- D08 PDP Conversion Scorecard ---------- */
const D08_CHECKS = [
  { c: "Title says what it is, for whom, and the key spec — in 8 words", fix: "Rewrite title as [product] for [user] with [differentiator]." },
  { c: "Images answer the top 3 buying questions without scrolling", fix: "Add a size/scale shot, an in-use shot, a proof close-up." },
  { c: "Price shows the deal honestly (MRP strike-through only if real)", fix: "Remove fake anchors; show per-unit price if packs differ." },
  { c: "Reviews: 50+ visible, recent, with photos; negatives answered", fix: "Trigger post-delivery review requests; answer the 3 worst publicly." },
  { c: "Delivery date and return policy visible before the fold", fix: "Pin 'Delivery by [date] · 7-day returns' beside the buy button." },
  { c: "One primary CTA, thumb-reachable, no competing buttons", fix: "Remove 'save for later' prominence; single buy action." },
  { c: "Specs as scannable bullets, not a paragraph", fix: "Convert description into 5 bullets: spec → benefit." },
  { c: "Comparison or 'why us' against the obvious alternative", fix: "Add a 3-row comparison with the category default." },
  { c: "Stock/urgency signals are true", fix: "Delete countdown timers that reset. Trust is conversion." },
  { c: "Page loads under 2.5s on a mid-range phone on 4G", fix: "Compress hero image; defer non-critical scripts." },
];
export function ToolD08() {
  const [scores, setScores] = useState({});
  const done = Object.keys(scores).length;
  const failed = D08_CHECKS.filter((_, i) => scores[i] === 0);
  const passed = Object.values(scores).filter((v) => v === 1).length;
  return (
    <div data-testid="tool-D08" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-3">
        <p className="text-sm text-zinc-600 mb-2">Open your best-selling product page on your phone. Pass/fail each check against what you actually see.</p>
        {D08_CHECKS.map((item, i) => (
          <div key={i} className="border border-zinc-200 p-3 flex items-center justify-between gap-4" data-testid={`d08-check-${i}`}>
            <span className="text-sm text-zinc-700 flex-1">{item.c}</span>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => setScores((s) => ({ ...s, [i]: 1 }))} className={`px-3 h-8 text-xs border transition-colors ${scores[i] === 1 ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`} data-testid={`d08-pass-${i}`}>Pass</button>
              <button type="button" onClick={() => setScores((s) => ({ ...s, [i]: 0 }))} className={`px-3 h-8 text-xs border transition-colors ${scores[i] === 0 ? "border-red-700 bg-red-700 text-white" : "border-zinc-300 hover:border-black"}`} data-testid={`d08-fail-${i}`}>Fail</button>
            </div>
          </div>
        ))}
      </div>
      <div>
        {done === D08_CHECKS.length ? (
          <div data-testid="d08-result">
            <ResultRow label="PDP score" value={`${passed} / ${D08_CHECKS.length}`} strong tone={passed >= 8 ? "good" : passed >= 6 ? undefined : "bad"} />
            {failed.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Revision actions, in order</div>
                <ol className="list-decimal pl-5 space-y-1.5 text-sm text-zinc-700">{failed.map((f, i) => <li key={i}>{f.fix}</li>)}</ol>
              </div>
            )}
            <Note>Fix order matters: the first failing check on this list usually gates the rest. A perfect spec sheet can't save a page that loads in six seconds.</Note>
          </div>
        ) : <Note>Score all ten checks. {done}/10 done.</Note>}
      </div>
    </div>
  );
}

/* ---------- D09 Marketplace Listing Audit ---------- */
const D09_CHECKS = [
  { c: "Title leads with the search term buyers actually type", gap: "Search-relevance gap: title written for brand, not for search." },
  { c: "All 7+ image slots used, incl. infographic and lifestyle", gap: "Empty image slots waste the only salesperson on the page." },
  { c: "A+ / brand content filled and current", gap: "A+ content lifts conversion; empty slots signal neglect to the algorithm too." },
  { c: "Rating ≥ 4.0 with 100+ reviews", gap: "Below 4.0, ads buy traffic that bounces — fix product/expectation first." },
  { c: "Price within 10% of the category's winning band", gap: "Outside the band you pay a conversion tax on every click." },
  { c: "Buy Box won ≥ 95% of the time", gap: "Losing the Buy Box means paying for someone else's sale." },
  { c: "Bullets answer: what, size, compatibility, in-box, warranty", gap: "Unanswered basics become returns and 1-star 'not as described'." },
  { c: "Backend keywords filled with synonyms and regional terms", gap: "Free indexing real estate, empty." },
  { c: "Q&A section seeded and answered within 48h", gap: "Unanswered questions age into doubt." },
];
export function ToolD09() {
  const [scores, setScores] = useState({});
  const done = Object.keys(scores).length;
  const failed = D09_CHECKS.filter((_, i) => scores[i] === 0);
  const passed = Object.values(scores).filter(Boolean).length;
  return (
    <div data-testid="tool-D09" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-3">
        <p className="text-sm text-zinc-600 mb-2">Open your top marketplace listing in an incognito tab — as a buyer sees it. Score honestly.</p>
        {D09_CHECKS.map((item, i) => (
          <div key={i} className="border border-zinc-200 p-3 flex items-center justify-between gap-4" data-testid={`d09-check-${i}`}>
            <span className="text-sm text-zinc-700 flex-1">{item.c}</span>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => setScores((s) => ({ ...s, [i]: 1 }))} className={`px-3 h-8 text-xs border transition-colors ${scores[i] === 1 ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`} data-testid={`d09-pass-${i}`}>Pass</button>
              <button type="button" onClick={() => setScores((s) => ({ ...s, [i]: 0 }))} className={`px-3 h-8 text-xs border transition-colors ${scores[i] === 0 ? "border-red-700 bg-red-700 text-white" : "border-zinc-300 hover:border-black"}`} data-testid={`d09-fail-${i}`}>Fail</button>
            </div>
          </div>
        ))}
      </div>
      <div>
        {done === D09_CHECKS.length ? (
          <div data-testid="d09-result">
            <ResultRow label="Listing health" value={`${passed} / ${D09_CHECKS.length}`} strong tone={passed >= 8 ? "good" : passed >= 6 ? undefined : "bad"} />
            <ResultRow label="Ad-spread verdict" value={passed >= 7 ? "Listing can convert paid traffic" : "Fix before scaling ads"} tone={passed >= 7 ? "good" : "bad"} />
            {failed.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Priority fixes</div>
                <ol className="list-decimal pl-5 space-y-1.5 text-sm text-zinc-700">{failed.map((f, i) => <li key={i}>{f.gap}</li>)}</ol>
              </div>
            )}
          </div>
        ) : <Note>Score all nine checks. {done}/9 done.</Note>}
      </div>
    </div>
  );
}

/* ---------- D16 Digital Commerce Maturity Assessment ---------- */
const D16_CAPS = [
  { key: "economics", label: "Unit economics", hint: "You know contribution per order by channel, weekly." },
  { key: "acquisition", label: "Acquisition engine", hint: "Paid, organic and CRM each measurable to contribution." },
  { key: "conversion", label: "Conversion craft", hint: "Funnel measured stage-wise; CRO backlog runs continuously." },
  { key: "retention", label: "Retention & CRM", hint: "Cohorts tracked; lifecycle messaging beyond the festival blast." },
  { key: "ops", label: "Fulfilment & service", hint: "Delivery promise kept >95%; returns costed and managed." },
  { key: "data", label: "Data & decision rhythm", hint: "One dashboard, one weekly review, decisions logged." },
];
export function ToolD16() {
  const [scores, setScores] = useState({});
  const done = Object.keys(scores).length;
  const avg = done ? Object.values(scores).reduce((a, b) => a + b, 0) / done : 0;
  const weakest = D16_CAPS.filter((c) => (scores[c.key] ?? 99) <= 2).map((c) => c.label);
  const band = avg >= 3.5 ? "Scaling" : avg >= 2.5 ? "Building" : avg >= 1.5 ? "Foundations" : "Starting";
  return (
    <div data-testid="tool-D16" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <p className="text-sm text-zinc-600 mb-2">Score each capability 0–4: 0 = absent, 1 = ad hoc, 2 = defined, 3 = measured, 4 = managed to targets.</p>
        {D16_CAPS.map((c) => (
          <div key={c.key} className="border border-zinc-200 p-4" data-testid={`d16-${c.key}`}>
            <div className="text-sm font-medium">{c.label}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{c.hint}</div>
            <div className="mt-2 flex gap-1.5">
              {[0, 1, 2, 3, 4].map((v) => (
                <button key={v} type="button" onClick={() => setScores((s) => ({ ...s, [c.key]: v }))}
                  className={`w-8 h-8 text-xs border transition-colors ${scores[c.key] === v ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`}
                  data-testid={`d16-${c.key}-${v}`}>{v}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div>
        {done === D16_CAPS.length ? (
          <div data-testid="d16-result">
            <Radar dims={D16_CAPS.map((c) => ({ label: c.label.split(" ")[0], score: (scores[c.key] / 4) * 100 }))} />
            <div className="mt-4">
              <ResultRow label="Maturity" value={`${(avg).toFixed(1)} / 4 — ${band}`} strong />
              <ResultRow label="Capabilities at or below 'defined'" value={weakest.length ? weakest.join(", ") : "None"} tone={weakest.length ? "bad" : "good"} />
            </div>
            <ToolSection title="Your 90-day sequence">
              <ol className="list-decimal pl-5 space-y-1.5 text-sm text-zinc-600">
                {(weakest.length ? weakest : ["Raise the bar: move two capabilities from 3 to 4"]).slice(0, 3).map((w, i) => (
                  <li key={i}><strong>Days {i * 30 + 1}–{i * 30 + 30}:</strong> {w} — one owner, one measurable 90-day move, reviewed monthly.</li>
                ))}
              </ol>
            </ToolSection>
          </div>
        ) : <Note>Score all six capabilities. {done}/6 done.</Note>}
      </div>
    </div>
  );
}
